import { useCallback, useEffect } from 'react';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Toast, showApolloErrorToUser, useForm } from '@pletly/ui';

import { passwordSchema, usernameSchema } from '@pletly/feature-auth';

import { useCreateCaregiverMutation } from './__generated__/createCaregiverMember.mutation.generated';
import { GetOrganisationDocument } from '../../../view/components/__generated__/organisation.generated';
import { useCreateCareReceiverMutation } from './__generated__/createCareReceiverMember.mutation.generated';
import { OrganisationsStackParamsList } from '../../../../navigation/types';

const PHONE_REGEX = new RegExp(/^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/);

const careGiverBaseFormSchema = z.object({
	username: usernameSchema,
	firstName: z.string(),
	lastName: z.string(),
	telephone: z
		.string()
		.optional()
		.refine((value) => !value || PHONE_REGEX.test(value)),
});
const careGiverFormSchema = z.union([
	careGiverBaseFormSchema.extend({
		withPassword: z.literal(false).default(false),
		email: z.string().email(),
	}),
	careGiverBaseFormSchema.extend({
		withPassword: z.literal(true).default(true),
		password: passwordSchema,
	}),
]);

const careReceiverFormSchemaBase = z.object({
	username: usernameSchema,
	firstName: z.string().optional(),
	lastName: z.string().optional(),
});
const careReceiverFormSchema = z.union([
	careReceiverFormSchemaBase.extend({
		withPassword: z.literal(false).default(false),
	}),
	careReceiverFormSchemaBase.extend({
		withPassword: z.literal(true).default(true),
		password: passwordSchema,
	}),
]);

export const useNewOrganisationMember = ({
	organisationUuid,
	type,
}: {
	organisationUuid: string;
	type: 'caregiver' | 'careRecipient';
}) => {
	const { replace } = useNavigation<NativeStackNavigationProp<OrganisationsStackParamsList>>();
	const { t } = useTranslation();
	const formSchema = type === 'caregiver' ? careGiverFormSchema : careReceiverFormSchema;
	const formMethods = useForm(formSchema);
	const { watch, resetField } = formMethods;
	const [createCaregiver, { loading: isCreatingCareGiver }] = useCreateCaregiverMutation({
		refetchQueries: [GetOrganisationDocument, 'SearchUsers'],
	});

	const withPassword = watch('withPassword');

	useEffect(() => {
		if (withPassword) {
			return;
		}

		resetField('password');
	}, [resetField, withPassword]);

	const [createCareRecipient, { loading: isCreatingCareRecipent }] = useCreateCareReceiverMutation({
		refetchQueries: [GetOrganisationDocument],
	});
	const loading = isCreatingCareGiver || isCreatingCareRecipent;

	const handleCreate = async (data: z.infer<typeof formSchema>) => {
		if (loading) {
			return;
		}

		try {
			if (type === 'caregiver') {
				await handleCreateCaregiver(data);
			} else {
				await handleCreateCareRecipient(data);
			}
		} catch (error) {
			console.error(error);
			showApolloErrorToUser(error as Error);
		}
	};

	const handleCreateCaregiver = useCallback(
		async ({
			username,
			email,
			password,
			firstName,
			lastName,
			telephone,
		}: z.infer<typeof careGiverFormSchema>) => {
			await createCaregiver({
				variables: {
					organisationUuid,
					caregiverData: {
						username,
						email,
						password,
						firstName,
						lastName,
						telephone,
					},
				},
			});

			Toast.show({
				text1: t('toasts.success.employee-created'),
				type: 'success',
			});

			replace('Home', { uuid: organisationUuid });
		},
		[createCaregiver, organisationUuid, t, replace],
	);

	const handleCreateCareRecipient = useCallback(
		async (formData: z.infer<typeof careReceiverFormSchema>) => {
			const { withPassword, ...careReceiverData } = formData;
			const { data } = await createCareRecipient({
				variables: {
					organisationUuid,
					careReceiverData: careReceiverData,
				},
			});

			if (!data) {
				return;
			}

			Toast.show({
				text1: t('toasts.success.care-recipient-created'),
				type: 'success',
			});

			replace('CareRecipientCreatedConfirmation', {
				organisationUuid,
				memberId: data?.createCareReceiverMember.id,
			});
		},
		[createCareRecipient, organisationUuid, replace, t],
	);

	const onSubmit = formMethods.handleSubmit(handleCreate);

	return {
		loading,
		formMethods,
		onSubmit,
	};
};
